import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useAlgoTileId, useValidateAlgo } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { CategoryPicker } from '@/components/ui/CategoryPicker.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectAlgoErrors, selectAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import { LIQUIDITY_POOLS } from '@/store/orders/algoOrders/state.ts';

export const AlgoLiquidityPool = () => {
  const tileId = useAlgoTileId();
  const { formatMessage } = useIntl();
  const track = useTrackEvent();

  const liquidityPool = useAppSelector((state) => {
    const tile = selectAlgoTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[AlgoSpot] Tile with id ${tileId} not found in state.`);
      return undefined;
    }

    if (tile.type !== 'Twap+Order' && tile.type !== 'NightjarOrder' && tile.type !== 'FalconOrder') {
      console.error(`[AlgoSpot] Tile with id ${tileId} is not a AlgoOrder.`);
      return undefined;
    }

    return tile.properties.liquidityPool;
  }, deepEqual);

  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));
  const liquidityPoolError = errors?.liquidityPool;

  const validateAlgo = useValidateAlgo();

  const handleChangeLiquidityPool = useCallback(
    (liquidityPools: string[]) => {
      track('Order action', 'Liquidity pool changed');
      validateAlgo({ liquidityPool: liquidityPools });
    },
    [validateAlgo],
  );

  const itemsList = useMemo(
    () =>
      LIQUIDITY_POOLS.map((option) => ({
        key: option,
        label: formatMessage({ id: `order.form.liquidityPool.${option}` }),
      })),
    [formatMessage],
  );

  return (
    <OrderRow labelId="order.form.liquidityPool">
      <OmsFieldErrorWrapper errors={liquidityPoolError}>
        <CategoryPicker
          id="liquidity-pool"
          placeholder={formatMessage({ id: 'order.form.liquidityPool.placeholder' })}
          handleChange={handleChangeLiquidityPool}
          items={LIQUIDITY_POOLS}
          item={liquidityPool}
          itemsList={itemsList}
          state={isDefined(liquidityPoolError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
